<script setup>
const img = useImage()
const nuxtApp = useNuxtApp()
const route = useRoute()
const config = useRuntimeConfig()
const router = useRouter()
const loadAnime = useState('loadAnime', () => false)
const user = useStrapiUser()
const bgLoaded = ref(false)
const onlineStats = useState('onlinePresenceStats', () => ({
  global: 0,
  anime: null,
  episode: null,
  episodes: null
}))

// Используем динамический ключ с параметром роута для правильного кэширования
const { pending, data: anime } = await useAsyncData(
  `anime-${route.params.name}`,
  () => $fetch(`${config.public.apiBase}/api/anime/${route.params.name}`),
  {
    getCachedData: (key) => {
      const nuxtApp = useNuxtApp()
      return nuxtApp.payload.data[key] || nuxtApp.static.data[key]
    }
  }
)

// Редирект на главную если аниме не найдено
watch(
  anime,
  (val) => {
    if (!pending.value && !val) {
      router.push('/')
    }
  },
  { immediate: true }
)

definePageMeta({
  middleware: 'auth-anime'
})

onMounted(async () => {
  if (process.client && anime.value?.privat && !user.value) {
    await hydrateStrapiUser()
  }

  // console.log(anime.value)
  if (process.client && window.zaraz) {
    window?.zaraz?.track(`anime_${router.currentRoute._value.params.name}`, {
      anime: router.currentRoute._value.params.name
    })
  }
})

const page = ref()
const { isFullscreen, enter, exit, toggle } = useFullscreen(page)

// Канонічна адреса сторінки — її ж віддаємо comments.app як адресу гілки
// коментарів до всього аніме
const requestUrl = useRequestURL()
const canonicalUrl = computed(
  () => `${requestUrl.origin}/watch/${route.params.name}`
)

// gtag('event', `anime_${router.currentRoute._value.params.name}`, {
//   anime: router.currentRoute._value.params.name
// })
</script>

<template>
  <div class="">
    <Head>
      <Title>{{ anime?.title }} - Олсіор дивиться Аніме</Title>
      <Link
        rel="canonical"
        :href="canonicalUrl"
      />
      <Meta
        name="og-title"
        hid="og-title"
        property="og:title"
        :content="anime?.title"
      />
      <Meta
        name="description"
        hid="description"
        :content="anime?.description"
      />
      <Meta
        name="og:description"
        hid="og:description"
        property="og:description"
        :content="anime?.description"
      />
      <Meta
        v-if="anime?.background != null"
        name="og:image"
        hid="og:image"
        property="og:image"
        :content="`${config.public.apiBase}${anime?.background?.url}`"
      />
    </Head>
    <div
      v-if="!pending"
      ref="page"
      class=""
    >
      <div class="">
        <div v-if="anime?.background">
          <Transition name="fade">
            <div
              v-show="bgLoaded"
              class="anime-bg"
            >
              <NuxtImg
                provider="ipx"
                :src="`${useStrapiMedia(anime?.background?.url)}`"
                format="webp"
                :loading="'lazy'"
                :alt="anime.title"
                class="w-full h-full object-cover"
                :class="`card-${anime.url}`"
                quality="100"
                @load="bgLoaded = true"
                :width="anime?.background?.width"
                :height="anime?.background?.height"
                sizes="100vw lg:1920px"
                placeholder
              />
            </div>
          </Transition>
          <!-- Gradient always visible — independent of image load -->
        </div>
        <div class="anime-bg-gradient" />
      </div>

      <div
        class="z-10 relative mx-auto px-[10px] lg:py-[20px] pb-[50px] max-w-[1350px] min-h-screen"
      >
        <div
          class="flex md:flex-row flex-col gap-[30px] lg:gap-[50px] pt-[30px]"
        >
          <div class="w-full md:w-[300px] md:min-w-[300px]">
            <div class="md:hidden block mb-[20px]">
              <WatchTitle :anime="anime" />
            </div>

            <div class="relative">
              <NuxtImg
                provider="ipx"
                :src="`${useStrapiMedia(anime?.poster.url)}`"
                format="webp"
                :alt="anime.title"
                class="bg-base-100 shadow-lg rounded-[12px] w-full min-h-[400px] lg:max-h-[400px] object-cover mobile-aspect"
                :class="`card-${anime.url}`"
                quality="100"
                :width="anime?.poster.width"
                :height="anime?.poster.height"
                sizes="800px lg:600px"
                placeholder
              />

              <div
                v-if="onlineStats.anime > 0"
                class="top-[10px] right-[10px] z-20 absolute flex items-center gap-[6px] bg-neutral/80 shadow-lg px-[5px] py-[2px] rounded-md w-fit font-medium text-neutral-content lg:text-md text-sm stars"
              >
                <span class="relative flex w-[8px] h-[8px]">
                  <span
                    class="inline-flex absolute bg-success opacity-75 rounded-full w-full h-full"
                  ></span>
                  <span
                    class="inline-flex relative bg-success rounded-full w-[8px] h-[8px]"
                  ></span>
                </span>
                {{ onlineStats.anime }}
              </div>

              <div
                v-if="anime.stars > 0 && !anime.tooltip"
                class="right-[10px] bottom-[10px] z-20 absolute flex items-center bg-neutral/60 shadow-lg px-[5px] py-[2px] rounded-md w-fit font-medium text-neutral-content lg:text-lg stars"
              >
                {{ anime.stars }}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  xmlns:xlink="http://www.w3.org/1999/xlink"
                  aria-hidden="true"
                  role="img"
                  class="iconify iconify--ic"
                  width="24"
                  height="24"
                  preserveAspectRatio="xMidYMid meet"
                  viewBox="0 0 24 24"
                >
                  <path
                    fill="currentColor"
                    d="m12 17.27l4.15 2.51c.76.46 1.69-.22 1.49-1.08l-1.1-4.72l3.67-3.18c.67-.58.31-1.68-.57-1.75l-4.83-.41l-1.89-4.46c-.34-.81-1.5-.81-1.84 0L9.19 8.63l-4.83.41c-.88.07-1.24 1.17-.57 1.75l3.67 3.18l-1.1 4.72c-.2.86.73 1.54 1.49 1.08l4.15-2.5z"
                  ></path>
                </svg>
              </div>
              <div
                v-if="!anime.watched && !anime.tooltip"
                class="right-[10px] bottom-[10px] z-20 absolute flex items-center bg-neutral/60 shadow-lg px-[5px] py-[2px] rounded-md w-fit font-medium text-neutral-content text-sm lg:text-lg stars"
              >
                {{ $t('watching') }}
              </div>
              <div
                v-if="anime.tooltip"
                class="right-[10px] bottom-[10px] z-20 absolute flex items-center bg-neutral/60 shadow-lg px-[5px] py-[2px] rounded-md w-fit max-w-[80%] font-medium text-[12px] text-neutral-content lg:text-[15px] stars"
                v-html="anime.tooltip"
              />
            </div>

            <WatchAnimeInfo :anime="anime" />
          </div>

          <div class="relative w-full">
            <div class="hidden lg:block">
              <WatchTitle :anime="anime" />
            </div>

            <WatchPlayer
              v-if="!pending"
              :anime="anime"
            />

            <LazyWatchDesc
              v-if="!isFullscreen"
              :anime="anime"
            />

            <LazyWatchInfo
              v-if="!isFullscreen"
              :anime="anime"
            />

            <LazyWatchComments
              v-if="!isFullscreen"
              :page-id="route.params.name"
              :title="$t('commentsanime')"
              :page-title="anime?.title"
              :page-url="canonicalUrl"
              :anime="String(route.params.name)"
            />

            <!-- <WatchBugReport /> -->

            <!-- <WatchComents
              v-if="!isFullscreen"
              :url="router.currentRoute._value.params.name"
            /> -->
          </div>
        </div>
      </div>
    </div>
    <MyFooter />
  </div>
</template>

<style>
.anime-bg {
  z-index: 1;
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 800px;
  overflow: hidden;
}

.anime-bg img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
}

.anime-bg-gradient {
  z-index: 2;
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 800px;
  pointer-events: none;
  background:
    linear-gradient(to top, hsla(var(--b1) / 1) 0%, transparent 60%),
    linear-gradient(to bottom, hsla(var(--b1) / 0.5) 0%, transparent 30%);
}

.showBg {
}

.active {
  background: hsla(var(--bc) / 1);
  color: hsla(var(--p) / 1);
}

.active:hover {
  background: hsla(var(--bc) / 1);
  color: hsla(var(--p) / 1);
}

.video-menu {
}

.emote {
  display: inline-block;
  height: 15px;
  width: auto;
  transform: translateY(0px);
}

@media screen and (min-width: 1024px) {
  .emote {
    display: inline-block;
    height: 25px;
    width: auto;
    transform: translateY(0px);
  }
}

.chat {
  /* scroll-behavior: smooth; */
}

.showLoading {
  /* animation: showlg 0.25s ease-in-out; */
}

@keyframes showlg {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes showBg {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}
</style>
